// AI views: AIImportView, ValidationView, ExtractionDashView,
// RedlineReview, DraftingView.
//
// AIImportView is the bulk PDF→contract import flow with batch
// processing, AI field extraction, parent matching, and dedup checks.
// ValidationView is the human review queue for AI-extracted fields.
// ExtractionDashView shows extraction stats and quality metrics.
// RedlineReview displays AI-suggested redlines from playbook analysis.
// DraftingView is the contract drafting assistant with chat + tools.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { diffWords } from "diff";
import { T } from "../theme";
import { fmtDate, toTitleCase } from "../utils";
import { apiCall } from "../apiClient";
import { AI_UNIVERSAL_FIELDS } from "../constants";
import { renderMarkdown } from "./markdown";
import { PdfHighlightModal } from "./PdfHighlightModal";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";

// Validate an extraction row before allowing import. Blocks approval when:
// - A field has a value with low confidence (AI extracted something but is uncertain)
// - A field value contains placeholder markers ([brackets], "insert here",
//   "TBD", "to be determined", etc.) — the AI hallucinated a placeholder
// - A field is empty but the AI is confident it SHOULD be present (rare)
//
// Does NOT block on: empty field + low confidence. That's the AI saying the
// field is not applicable / not in this document — a normal case (e.g., an MSA
// doesn't have "Amendment Effective Date" or "Sub-processor Requirements").
// Returns { ok, lowConf: string[], missing: string[], placeholder: string[] }.
const PLACEHOLDER_RE = /[\[\]<>{}]|insert\s|tbd|to\s+be\s+determined|placeholder|\bn\/?a\b/i;
const validateImportRow = (row: any): { ok: boolean; lowConf: string[]; missing: string[]; placeholder: string[]; blocking: string[] } => {
  const lowConf: string[] = [];
  const missing: string[] = [];
  const placeholder: string[] = [];
  Object.entries(row).forEach(([k, v]: [string, any]) => {
    if (k.startsWith('__') || k === 'suggestedRelationship') return;
    if (!v || typeof v !== 'object') return;
    const val = (v.v == null ? '' : String(v.v)).trim();
    const conf = (v.conf || v.confidence || 'medium').toLowerCase();
    if (!val) { if (conf !== 'low') missing.push(k); return; }
    if (PLACEHOLDER_RE.test(val)) { placeholder.push(k); return; }
    if (conf === 'low') { lowConf.push(k); }
  });
  const blocking = [...missing, ...placeholder, ...lowConf];
  return { ok: blocking.length === 0, lowConf, missing, placeholder, blocking };
};


/* ═══════════════════ SECURITY VIEW ═══════════════════ */
export const SecurityView = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/security").then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.txM }}>Loading...</div>;
  if (!data) return <div style={{ padding: 40, textAlign: "center", color: T.red }}>Failed to load security information.</div>;

  const s = data.security;

  const SecurityCard = ({ icon, title, children }: { icon?: any; title?: any; children?: any }) => (
    <div style={{ backgroundColor: T.bg2, borderRadius: 10, border: `1px solid ${T.brd}`, padding: "24px 28px", display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <span style={{ fontSize: 22 }}>{icon}</span>
        <span style={{ fontSize: 15, fontWeight: 700, color: T.tx }}>{title}</span>
      </div>
      <div style={{ fontSize: 12.5, lineHeight: 1.7, color: T.txM }}>{children}</div>
    </div>
  );

  const Label = ({ children }: { children?: any }) => <div style={{ fontSize: 11, fontWeight: 700, color: T.tx, textTransform: "uppercase", letterSpacing: ".04em", marginTop: 6, marginBottom: 2 }}>{children}</div>;

  return (
    <div style={{ flex: 1, overflow: "auto", padding: "32px 40px", maxWidth: 960, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 36 }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: T.tx, fontFamily: "'Source Serif 4', serif", marginBottom: 8 }}>Security & Compliance</div>
        <div style={{ fontSize: 14, color: T.txM, maxWidth: 600, margin: "0 auto", lineHeight: 1.6 }}>
          KontractAI is built with enterprise-grade security practices. This page documents our security posture, data handling procedures, and compliance measures.
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 20, marginBottom: 32 }}>

        <SecurityCard icon="🔐" title="Encryption">
          <Label>In Transit</Label>
          <div>{s.encryption_in_transit}</div>
          <Label>At Rest</Label>
          <div>{s.encryption_at_rest}</div>
        </SecurityCard>

        <SecurityCard icon="🛡" title="Authentication & Authorization">
          <Label>Authentication</Label>
          <div>{s.authentication}</div>
          <Label>Authorization</Label>
          <div>{s.authorization}</div>
          <Label>Password Policy</Label>
          <div>{s.password_policy}. Optional TOTP two-factor authentication.</div>
          <Label>Two-Factor Authentication</Label>
          <div>TOTP-based 2FA compatible with Microsoft Authenticator, Google Authenticator, and all TOTP-compliant apps. 6-digit codes with 30-second rotation. 8 single-use recovery codes. Admin-enforceable per user. 5-minute temp token for challenge. SSO users authenticate via identity provider MFA.</div>
        </SecurityCard>

        <SecurityCard icon="◆" title="AI Data Handling">
          <div style={{ backgroundColor: T.accS, border: `1px solid ${T.accS2}`, borderRadius: 8, padding: "14px 16px", marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.acc, marginBottom: 6 }}>Anthropic does not train on your data</div>
            <div style={{ fontSize: 12, color: T.txM, lineHeight: 1.6 }}>{s.ai_processing.data_retention}</div>
          </div>
          <Label>Provider & Model</Label>
          <div>{s.ai_processing.provider} — {s.ai_processing.model}</div>
          <Label>Data Handling</Label>
          <div>{s.ai_processing.data_handling}</div>
          <div style={{ marginTop: 8 }}>
            <a href={s.ai_processing.no_training_link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: T.acc, fontWeight: 600, textDecoration: "none" }}>
              View Anthropic Privacy Policy →
            </a>
          </div>
        </SecurityCard>

        {s.sso && (
          <SecurityCard icon="🔑" title="Single Sign-On (SSO)">
            <Label>Provider</Label>
            <div>{s.sso.provider}</div>
            <Label>Protocol</Label>
            <div>{s.sso.protocol}</div>
            <Label>Tenant</Label>
            <div>{s.sso.tenant}</div>
            <Label>Auto-Provisioning</Label>
            <div>{s.sso.auto_provisioning}</div>
          </SecurityCard>
        )}

        <SecurityCard icon="📋" title="Audit & Logging">
          <div>{s.audit_logging}</div>
        </SecurityCard>

        <SecurityCard icon="🏗" title="Infrastructure">
          <Label>Hosting</Label>
          <div>{s.infrastructure.hosting}</div>
          <Label>Containers</Label>
          <div>{s.infrastructure.containers}</div>
          <Label>Database</Label>
          <div>{s.infrastructure.database}</div>
          <Label>Backups</Label>
          <div>{s.infrastructure.backups}</div>
          <Label>SSL</Label>
          <div>{s.infrastructure.ssl}</div>
        </SecurityCard>

        <SecurityCard icon="🔧" title="Security Headers">
          {(Object.entries(s.headers || {}) as [string, string][]).map(([k, v]) => (
            <div key={k} style={{ fontFamily: "monospace", fontSize: 11, backgroundColor: T.bg3, padding: "4px 8px", borderRadius: 4, marginBottom: 4 }}>{v}</div>
          ))}
          <Label>Rate Limiting</Label>
          <div>{s.rate_limiting}</div>
          <Label>File Uploads</Label>
          <div>{s.file_uploads}</div>
        </SecurityCard>

        <SecurityCard icon="📄" title="Document Security">
          <Label>Document Tagging</Label>
          <div>All uploaded .docx files are embedded with a custom XML property linking them to their contract request. Enables chain of custody tracking and automated association across platforms.</div>
          <Label>SharePoint Sync</Label>
          <div>Documents are automatically synced to SharePoint Online with structured naming conventions. Final drafts are additionally archived to the Contract Repository. Per-version re-sync for transient failures.</div>
          <Label>Version Control</Label>
          <div>All document versions are tracked with SHA-256 checksums, uploader identity, and timestamps. Version history is immutable. Draft labels (Internal, Company, Counterparty, Final) trigger automated workflow transitions.</div>
        </SecurityCard>

        <SecurityCard icon="📝" title="Word Add-in Security">
          <Label>Authentication</Label>
          <div>JWT-based with full 2FA support. Tokens stored in browser localStorage with separate key from web app. 5-minute TOTP challenge window.</div>
          <Label>Transport</Label>
          <div>All API calls from the add-in use HTTPS (TLS 1.2+). Office iframe embedding controlled via Content-Security-Policy frame-ancestors directive allowing only *.office.com and *.microsoft.com origins.</div>
          <Label>Document Association</Label>
          <div>Custom XML properties embedded in documents enable automatic request detection. Namespace: kontractai.com/document-properties. Read-write permission scoped to active document only.</div>
        </SecurityCard>

      </div>

      <div style={{ textAlign: "center", padding: "20px 0 40px", borderTop: `1px solid ${T.brd}` }}>
        <div style={{ fontSize: 12, color: T.txD }}>{data.contact}</div>
        <div style={{ fontSize: 11, color: T.txD, marginTop: 6 }}>Last updated: {data.last_updated}</div>
      </div>
    </div>
  );
};
